import React, { createContext, useContext, useEffect, useState } from 'react';
import { Task } from '../lib/tasks';
import { useTasks } from './TasksContext';

interface UnreadTasksContextType {
  unreadTasks: Task[];
  markAsRead: (taskId: string) => void;
  markAllAsRead: () => void;
  unreadCount: number;
}

const UnreadTasksContext = createContext<UnreadTasksContextType | undefined>(undefined);

export function UnreadTasksProvider({ children }: { children: React.ReactNode }) {
  const { tasks } = useTasks();
  const [unreadTasks, setUnreadTasks] = useState<Task[]>([]);
  
  // Load unread tasks from localStorage on mount
  useEffect(() => {
    const savedUnreadTasks = localStorage.getItem('unreadTasks');
    if (savedUnreadTasks) {
      setUnreadTasks(JSON.parse(savedUnreadTasks));
    }
  }, []);

  // Save unread tasks to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('unreadTasks', JSON.stringify(unreadTasks));
  }, [unreadTasks]);

  // Listen for new tasks and add them to unread
  useEffect(() => {
    const lastCheck = localStorage.getItem('lastTaskCheck');
    const lastCheckTime = lastCheck ? new Date(lastCheck).getTime() : 0;

    const newTasks = tasks.filter(task => {
      const taskTime = new Date(task.created_at).getTime();
      return taskTime > lastCheckTime;
    });

    if (newTasks.length > 0) {
      setUnreadTasks(prev => {
        const uniqueTasks = newTasks.filter(
          newTask => !prev.some(task => task.id === newTask.id)
        );
        return [...prev, ...uniqueTasks];
      });
    }

    // Update last check time
    localStorage.setItem('lastTaskCheck', new Date().toISOString());
  }, [tasks]);

  const markAsRead = (taskId: string) => {
    setUnreadTasks(prev => prev.filter(task => task.id !== taskId));
  };

  const markAllAsRead = () => {
    setUnreadTasks([]);
    localStorage.setItem('lastTaskCheck', new Date().toISOString());
  };

  const value = {
    unreadTasks,
    markAsRead,
    markAllAsRead,
    unreadCount: unreadTasks.length
  };

  return (
    <UnreadTasksContext.Provider value={value}>
      {children}
    </UnreadTasksContext.Provider>
  );
}

export function useUnreadTasks() {
  const context = useContext(UnreadTasksContext);
  if (context === undefined) {
    throw new Error('useUnreadTasks must be used within an UnreadTasksProvider');
  }
  return context;
} 